import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, ClipboardCheck, MessageSquareText, Pencil } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import SupportRecordSheet from "@/components/SupportRecordSheet";
import { TypedDateInput } from "@/components/TypedDateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

export default function ActiveJobWorkspacePage() {
  const { jobId } = useParams();
  const {
    canManageActiveJobs,
    canManageCommunications,
    canManageRecords,
  } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("permit");
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    const response = await axios.get(`${API}/active-jobs/${jobId}`);
    setData(response.data);
    setForm(response.data.active_job);
  }, [jobId]);
  const loadAssignableUsers = useCallback(async () => {
    const response = await axios.get(`${API}/assignable-users`);
    setUsers(response.data.users);
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Could not load this field assignment."));
  }, [load]);
  useEffect(() => {
    loadAssignableUsers().catch(() => toast.error("Assignable users could not be loaded."));
  }, [loadAssignableUsers]);

  if (!data || !form) {
    return (
      <div className="loading-screen" data-testid="active-job-detail-loading">
        Loading field assignment…
      </div>
    );
  }

  const { active_job: job, project } = data;
  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };
  const openEditor = (nextMode, record) => {
    setMode(nextMode);
    setSelected(record);
  };
  const cancelEdit = () => {
    setForm(job);
    setIsEditing(false);
  };
  const save = async (event) => {
    event.preventDefault();
    try {
      await axios.patch(`${API}/active-jobs/${job.id}`, {
        scheduled_date: form.scheduled_date,
        assigned_user_ids: form.assigned_user_ids || [],
        foreman: form.foreman,
        assignment_notes: form.assignment_notes,
        status: form.status,
        crew_members:
          typeof form.crew_members === "string"
            ? form.crew_members
                .split(",")
                .map((name) => name.trim())
                .filter(Boolean)
            : form.crew_members,
      });
      setIsEditing(false);
      toast.success("Field assignment saved.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Field assignment could not be saved.");
    }
  };

  return (
    <main className="detail-page" data-testid="active-job-detail-page">
      <Link
        className="back-link"
        data-testid="back-to-project-link"
        to={`/projects/${project.id}`}
      >
        <ArrowLeft size={16} />
        {project.name || "Project"}
      </Link>
      <header className="detail-header">
        <div>
          <span className="ops-eyebrow">FIELD ASSIGNMENT · {job.scheduled_date}</span>
          <h1 data-testid="active-job-detail-heading">
            {project.name || "Unlinked project"}
          </h1>
          <p>{job.foreman || "No foreman assigned"}</p>
        </div>
        {canManageActiveJobs && (
          <button
            data-testid="edit-active-job-button"
            onClick={() => setIsEditing((current) => !current)}
            type="button"
          >
            <Pencil size={15} />
            {isEditing ? "Close edit" : "Edit assignment"}
          </button>
        )}
      </header>

      {isEditing && (
        <form className="project-edit-form" data-testid="active-job-edit-form" onSubmit={save}>
          <label htmlFor="job-scheduled-date-input">
            Scheduled date
            <TypedDateInput
              data-testid="job-scheduled-date-input"
              id="job-scheduled-date-input"
              onChange={(value) => updateField("scheduled_date", value)}
              value={form.scheduled_date || ""}
            />
          </label>
          <label>Assigned crew / assisted users<select multiple data-testid="active-job-crew-assignment-select" onChange={(event) => updateField("assigned_user_ids", Array.from(event.target.selectedOptions, (option) => option.value))} value={form.assigned_user_ids || []}>{users.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}</select></label>
          <label htmlFor="job-foreman-input">
            Foreman
            <input
              data-testid="job-foreman-input"
              id="job-foreman-input"
              onChange={(event) => updateField("foreman", event.target.value)}
              value={form.foreman || ""}
            />
          </label>
          <label htmlFor="job-crew-input">
            Crew members
            <input
              data-testid="job-crew-input"
              id="job-crew-input"
              onChange={(event) => updateField("crew_members", event.target.value)}
              value={
                Array.isArray(form.crew_members)
                  ? form.crew_members.join(", ")
                  : form.crew_members || ""
              }
            />
          </label>
          <label className="wide-field" htmlFor="job-assignment-notes-input">
            Assignment notes
            <textarea
              data-testid="job-assignment-notes-input"
              id="job-assignment-notes-input"
              onChange={(event) => updateField("assignment_notes", event.target.value)}
              value={form.assignment_notes || ""}
            />
          </label>
          <div className="active-job-edit-actions">
            <button
              data-testid="cancel-active-job-button"
              onClick={cancelEdit}
              type="button"
            >
              Cancel
            </button>
            <button data-testid="save-active-job-button" type="submit">
              Save field assignment
            </button>
          </div>
        </form>
      )}

      <section className="job-plan" data-testid="active-job-plan">
        <span className="ops-eyebrow">TODAY’S ASSIGNMENT</span>
        <p>{job.assignment_notes || "No assignment notes recorded."}</p>
        <div>
          {Array.isArray(job.crew_members)
            ? job.crew_members.map((member) => <span key={member}>{member}</span>)
            : null}
        </div>
      </section>

      <section className="detail-grid">
        <RecordList
          canEdit={canManageRecords}
          heading="Permits & Locates"
          items={data.permits}
          mode="permit"
          onOpen={openEditor}
        />
        <RecordList
          canEdit={canManageCommunications}
          heading="Project follow-ups"
          items={data.communications}
          mode="communication"
          onOpen={openEditor}
        />
      </section>
      <SupportRecordSheet
        mode={mode}
        onOpenChange={() => setSelected(null)}
        onSaved={load}
        open={Boolean(selected)}
        record={selected}
      />
      <Toaster richColors position="bottom-right" />
    </main>
  );
}

function RecordList({ canEdit, heading, items, mode, onOpen }) {
  const Icon = mode === "permit" ? ClipboardCheck : MessageSquareText;
  return (
    <section>
      <div className="detail-section-title">
        <Icon size={17} />
        <h2>{heading}</h2>
      </div>
      <div className="record-list">
        {items.map((item) => {
          return canEdit ? (
            <button
              className="record-row"
              data-testid={`edit-job-${mode}-${item.id}`}
              key={item.id}
              onClick={() => onOpen(mode, item)}
              type="button"
            >
              <RecordListContent item={item} mode={mode} />
            </button>
          ) : (
            <article
              className="record-row record-row-read-only"
              data-testid={`view-job-${mode}-${item.id}`}
              key={item.id}
            >
              <RecordListContent item={item} mode={mode} />
            </article>
          );
        })}
      </div>
    </section>
  );
}

function RecordListContent({ item, mode }) {
  const detail =
    mode === "permit"
      ? item.reference_number || "No reference"
      : `${item.assignee || "Unassigned"} · due ${item.due_at || "not set"}`;
  const title = mode === "permit" ? item.permit_type : item.title;

  return (
    <>
      <div>
        <strong>{title}</strong>
        <span>{detail}</span>
      </div>
      <span>{item.status}</span>
    </>
  );
}